import "core-js/modules/es.symbol.description.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { http, HttpResponse } from "msw";
import { runInAction } from "mobx";
import JulianDate from "terriajs-cesium/Source/Core/JulianDate";
import CustomDataSource from "terriajs-cesium/Source/DataSources/CustomDataSource";
import LabelStyle from "terriajs-cesium/Source/Scene/LabelStyle";
import { getMakiIcon } from "../../lib/Map/Icons/Maki/MakiIcons";
import { ImageryParts } from "../../lib/ModelMixins/MappableMixin";
import CsvCatalogItem from "../../lib/Models/Catalog/CatalogItems/CsvCatalogItem";
import CommonStrata from "../../lib/Models/Definition/CommonStrata";
import createStratumInstance from "../../lib/Models/Definition/createStratumInstance";
import updateModelFromJson from "../../lib/Models/Definition/updateModelFromJson";
import Terria from "../../lib/Models/Terria";
import { worker } from "../mocks/browser";
import TableColorStyleTraits from "../../lib/Traits/TraitsClasses/Table/ColorStyleTraits";
import TableLabelStyleTraits, { EnumLabelSymbolTraits, LabelSymbolTraits } from "../../lib/Traits/TraitsClasses/Table/LabelStyleTraits";
import TableOutlineStyleTraits, { BinOutlineSymbolTraits, EnumOutlineSymbolTraits } from "../../lib/Traits/TraitsClasses/Table/OutlineStyleTraits";
import TablePointStyleTraits, { BinPointSymbolTraits, EnumPointSymbolTraits, PointSymbolTraits } from "../../lib/Traits/TraitsClasses/Table/PointStyleTraits";
import TableStyleTraits from "../../lib/Traits/TraitsClasses/Table/StyleTraits";
import TableTimeStyleTraits from "../../lib/Traits/TraitsClasses/Table/TimeStyleTraits";
import TableTrailStyleTraits, { BinTrailSymbolTraits, EnumTrailSymbolTraits } from "../../lib/Traits/TraitsClasses/Table/TrailStyleTraits";
import HorizontalOrigin from "terriajs-cesium/Source/Scene/HorizontalOrigin";
import VerticalOrigin from "terriajs-cesium/Source/Scene/VerticalOrigin";
import ScaleByDistanceTraits from "../../lib/Traits/TraitsClasses/ScaleByDistanceTraits";
import LatLonValCsv from "../../wwwroot/test/csv/lat_lon_val.csv";
import LatLonEnumCsv from "../../wwwroot/test/csv/lat_lon_enum.csv";
import LatLonValCsvDuplicate from "../../wwwroot/test/csv/lat_lon_val_with_duplicate_row.csv";
import LatLonEnumDateIdCsv from "../../wwwroot/test/csv/lat_lon_enum_date_id.csv";
import LatLonEnumDateIdWithRegionCsv from "../../wwwroot/test/csv/lat_lon_enum_date_id_with_regions.csv";
import LgaWithDisambigCsv from "../../wwwroot/test/csv/lga_state_disambig.csv";
import ParkingSensorDataCsv from "../../wwwroot/test/csv/parking-sensor-data.csv";
import LegendDecimalPlacesCsv from "../../wwwroot/test/csv/legend-decimal-places.csv";
import BadDatesCsv from "../../wwwroot/test/csv/bad-dates.csv";
import regionMapping from "../../wwwroot/data/regionMapping.json";
import additionalRegionMapping from "../../wwwroot/test/regionMapping/additionalRegion.json";
import regionIdsSte from "../../wwwroot/data/regionids/region_map-STE_2016_AUST_STE_NAME16.json";
import regionIdsLgaName from "../../wwwroot/data/regionids/region_map-FID_LGA_2011_AUST_LGA_NAME11.json";
import regionIdsLgaCode from "../../wwwroot/data/regionids/region_map-FID_LGA_2015_AUST_LGA_CODE15.json";
import regionIdsLgaNameStates from "../../wwwroot/data/regionids/region_map-FID_LGA_2011_AUST_STE_NAME11.json";
const NUMBER_OF_REGION_MAPPING_TYPES = 154;
describe("TableMixin", function () {
  let item;
  let terria;
  beforeEach(function () {
    terria = new Terria({
      baseUrl: "./"
    });
    item = new CsvCatalogItem("test", terria, undefined);
    worker.use(http.get("*/build/TerriaJS/data/regionMapping.json", () => HttpResponse.json(regionMapping)), http.get("https://tiles.terria.io/region-mapping/regionids/region_map-STE_2016_AUST_STE_NAME16.json", () => HttpResponse.json(regionIdsSte)), http.get("https://tiles.terria.io/region-mapping/regionids/region_map-FID_LGA_2011_AUST_LGA_NAME11.json", () => HttpResponse.json(regionIdsLgaName)), http.get("https://tiles.terria.io/region-mapping/regionids/region_map-FID_LGA_2015_AUST_LGA_CODE15.json", () => HttpResponse.json(regionIdsLgaCode)), http.get("https://tiles.terria.io/region-mapping/regionids/region_map-FID_LGA_2011_AUST_STE_NAME11.json", () => HttpResponse.json(regionIdsLgaNameStates)));
  });
  describe("when the table has time, lat/lon and id columns", function () {
    let dataSource;
    beforeEach(async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      (await item.loadMapItems()).throwIfError();
      dataSource = item.mapItems[0];
      expect(dataSource instanceof CustomDataSource).toBe(true);
    });
    it("creates one entity per id", function () {
      expect(item.activeTableStyle.rowGroups.length).toBe(4);
      if (dataSource instanceof CustomDataSource) {
        expect(dataSource.entities.values.length).toBe(4);
      }
    });
    it("sets showInChartPanel to false - as is mappable", function () {
      expect(item.showInChartPanel).toBeFalsy();
    });
    it("sets showInChartPanel to true - when lat/lon is disabled", function () {
      updateModelFromJson(item, CommonStrata.definition, {
        columns: [{
          name: "lat",
          type: "scalar"
        }, {
          name: "lon",
          type: "scalar"
        }]
      });
      expect(item.showInChartPanel).toBeTruthy();
    });
    it("doesn't show regions - even if empty region column is detected", () => {});
    it("calculates rectangle", function () {
      expect(item.rectangle.north).toEqual(-20);
      expect(item.rectangle.south).toEqual(-37);
      expect(item.rectangle.east).toEqual(155);
      expect(item.rectangle.west).toEqual(115);
    });
    describe("the entities", function () {
      it("has availability defined over the correct span", function () {
        expect(dataSource.entities.values.map(e => {
          var _e$availability;
          return (_e$availability = e.availability) === null || _e$availability === void 0 ? void 0 : _e$availability.start.toString();
        })).toEqual(["2015-08-01T00:00:00Z", "2015-08-01T00:00:00Z", "2015-08-02T00:00:00Z", "2015-08-03T00:00:00Z"]);
        expect(dataSource.entities.values.map(e => {
          var _e$availability2;
          return (_e$availability2 = e.availability) === null || _e$availability2 === void 0 ? void 0 : _e$availability2.stop.toString();
        })).toEqual(["2015-08-07T06:00:00Z", "2015-08-07T00:00:00Z", "2015-08-02T23:59:59Z", "2015-08-05T00:00:00Z"]);
      });
    });
    describe("when timeColumn is `null`", function () {
      it("returns an empty `discreteTimes`", function () {
        var _item$discreteTimes;
        expect((_item$discreteTimes = item.discreteTimes) === null || _item$discreteTimes === void 0 ? void 0 : _item$discreteTimes.length).toBe(6);
        item.defaultStyle.time.setTrait(CommonStrata.user, "timeColumn", null);
        expect(item.discreteTimes).toBe(undefined);
      });
      it("creates entities for all times", async function () {
        item.defaultStyle.time.setTrait(CommonStrata.user, "timeColumn", null);
        (await item.loadMapItems()).throwIfError();
        const mapItem = item.mapItems[0];
        expect(mapItem instanceof CustomDataSource).toBe(true);
        if (mapItem instanceof CustomDataSource) {
          expect(mapItem.entities.values.length).toBe(13);
        }
      });
    });
  });

  // Note this is identical to "when the table has time, lat/lon and id columns" EXCEPT
  // - one additional spec - "doesn't show regions - even if empty region column is detected"
  // - "sets showInChartPanel to true - when lat/lon is disabled" is replaced by "shows regions when lat/lon is disabled"
  describe("when the table has time, lat/lon, id columns AND regions", function () {
    let dataSource;
    beforeEach(async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdWithRegionCsv);
      (await item.loadMapItems()).throwIfError();
      dataSource = item.mapItems[0];
      expect(dataSource instanceof CustomDataSource).toBe(true);
    });
    it("creates one entity per id", function () {
      expect(item.activeTableStyle.rowGroups.length).toBe(4);
      if (dataSource instanceof CustomDataSource) {
        expect(dataSource.entities.values.length).toBe(4);
      }
    });
    it("sets showInChartPanel to false - as is mappable", function () {
      expect(item.showInChartPanel).toBeFalsy();
    });
    it("shows regions when lat/lon is disabled", function () {
      var _item$activeTableStyl;
      updateModelFromJson(item, CommonStrata.definition, {
        columns: [{
          name: "lat",
          type: "scalar"
        }, {
          name: "lon",
          type: "scalar"
        }]
      });
      expect(item.showInChartPanel).toBeFalsy();
      expect(item.showingRegions).toBeTruthy();
      expect((_item$activeTableStyl = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl === void 0 ? void 0 : _item$activeTableStyl.valuesAsRegions.uniqueRegionIds.length).toBe(3);
    });
    it("doesn't show regions - as more points are detected than unique regions", () => {
      expect(item.showingRegions).toBeFalsy();
    });
    it("calculates rectangle", function () {
      expect(item.rectangle.north).toEqual(-20);
      expect(item.rectangle.south).toEqual(-37);
      expect(item.rectangle.east).toEqual(155);
      expect(item.rectangle.west).toEqual(115);
    });
    describe("the entities", function () {
      it("has availability defined over the correct span", function () {
        expect(dataSource.entities.values.map(e => {
          var _e$availability3;
          return (_e$availability3 = e.availability) === null || _e$availability3 === void 0 ? void 0 : _e$availability3.start.toString();
        })).toEqual(["2015-08-01T00:00:00Z", "2015-08-01T00:00:00Z", "2015-08-02T00:00:00Z", "2015-08-03T00:00:00Z"]);
        expect(dataSource.entities.values.map(e => {
          var _e$availability4;
          return (_e$availability4 = e.availability) === null || _e$availability4 === void 0 ? void 0 : _e$availability4.stop.toString();
        })).toEqual(["2015-08-07T06:00:00Z", "2015-08-07T00:00:00Z", "2015-08-02T23:59:59Z", "2015-08-05T00:00:00Z"]);
      });
    });
    describe("when timeColumn is `null`", function () {
      it("returns an empty `discreteTimes`", function () {
        var _item$discreteTimes2;
        expect((_item$discreteTimes2 = item.discreteTimes) === null || _item$discreteTimes2 === void 0 ? void 0 : _item$discreteTimes2.length).toBe(6);
        item.defaultStyle.time.setTrait(CommonStrata.user, "timeColumn", null);
        expect(item.discreteTimes).toBe(undefined);
      });
      it("creates entities for all times", async function () {
        item.defaultStyle.time.setTrait(CommonStrata.user, "timeColumn", null);
        (await item.loadMapItems()).throwIfError();
        const mapItem = item.mapItems[0];
        expect(mapItem instanceof CustomDataSource).toBe(true);
        if (mapItem instanceof CustomDataSource) {
          expect(mapItem.entities.values.length).toBe(13);
        }
      });
    });
  });
  describe("when the table has lat/lon columns but no time & id columns", function () {
    it("creates one entity per row", async function () {
      runInAction(() => item.setTrait(CommonStrata.user, "csvString", LatLonValCsv));
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      expect(mapItem instanceof CustomDataSource).toBe(true);
      if (mapItem instanceof CustomDataSource) {
        expect(mapItem.entities.values.length).toBe(5);
      }
    });
    it("removes duplicate rows when requested", async function () {
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonValCsvDuplicate);
        item.setTrait(CommonStrata.user, "removeDuplicateRows", true);
      });
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      expect(mapItem instanceof CustomDataSource).toBe(true);
      if (mapItem instanceof CustomDataSource) {
        expect(mapItem.entities.values.length).toBe(5);
        const duplicateValue = 7;
        let occurrences = 0;
        for (const entity of mapItem.entities.values) {
          var _entity$properties;
          const val = (_entity$properties = entity.properties) === null || _entity$properties === void 0 ? void 0 : _entity$properties.value.getValue();
          if (val === duplicateValue) {
            occurrences++;
          }
        }
        expect(occurrences).toBe(1);
      }
    });
    it("has the correct property names", async function () {
      var _dataSource$entities$;
      runInAction(() => item.setTrait(CommonStrata.user, "csvString", LatLonValCsv));
      (await item.loadMapItems()).throwIfError();
      const dataSource = item.mapItems[0];
      const propertyNames = (_dataSource$entities$ = dataSource.entities.values[0].properties) === null || _dataSource$entities$ === void 0 ? void 0 : _dataSource$entities$.propertyNames;
      expect(propertyNames).toEqual(["lat", "lon", "value"]);
    });
  });
  describe("when the time column has bad datetimes in it", function () {
    it("ignores them gracefully", async function () {
      runInAction(() => item.setTrait(CommonStrata.user, "csvString", BadDatesCsv));
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      expect(mapItem instanceof CustomDataSource).toBe(true);
      if (mapItem instanceof CustomDataSource) {
        expect(mapItem.entities.values.length).toBe(3);
      }
    });
  });
  describe("when the table has time-series points with intervals", function () {
    let dataSource;
    beforeEach(async function () {
      item.setTrait(CommonStrata.user, "csvString", ParkingSensorDataCsv);
      (await item.loadMapItems()).throwIfError();
      dataSource = item.mapItems[0];
      expect(dataSource instanceof CustomDataSource).toBe(true);
    });
    it("creates one entity per id", function () {
      expect(dataSource.entities.values.length).toBe(21);
    });
    it("creates correct intervals", function () {
      var _item$activeTableStyl2, _item$activeTableStyl3;
      expect((_item$activeTableStyl2 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl2 === void 0 ? void 0 : _item$activeTableStyl2.length).toBe(21);
      expect(item.disableDateTimeSelector).toBeFalsy();
      expect((_item$activeTableStyl3 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl3 === void 0 ? void 0 : _item$activeTableStyl3.map(t => [t === null || t === void 0 ? void 0 : t.start.toString(), t === null || t === void 0 ? void 0 : t.stop.toString()])).toEqual([["2021-06-25T10:39:02Z", "2021-06-26T10:39:01Z"], ["2021-06-25T10:26:45Z", "2021-06-26T10:26:44Z"], ["2021-06-25T10:18:01Z", "2021-06-26T10:18:00Z"], ["2021-06-25T09:53:52Z", "2021-06-26T09:53:51Z"], ["2021-06-25T09:51:32Z", "2021-06-26T09:51:31Z"], ["2021-06-25T09:47:06Z", "2021-06-26T09:47:05Z"], ["2021-06-25T09:19:21Z", "2021-06-26T09:19:20Z"], ["2021-06-25T09:14:36Z", "2021-06-26T09:14:35Z"], ["2021-06-25T09:06:47Z", "2021-06-26T09:06:46Z"], ["2021-06-25T09:01:32Z", "2021-06-26T09:01:31Z"], ["2021-06-25T08:25:09Z", "2021-06-26T08:25:08Z"], ["2021-06-25T07:22:15Z", "2021-06-26T07:22:14Z"], ["2021-06-25T06:10:52Z", "2021-06-26T06:10:51Z"], ["2021-06-25T04:39:45Z", "2021-06-26T04:39:44Z"], ["2021-06-25T03:46:13Z", "2021-06-26T03:46:12Z"], ["2021-06-25T00:29:26Z", "2021-06-26T00:29:25Z"], ["2021-06-25T00:27:23Z", "2021-06-26T00:27:22Z"], ["2021-06-24T14:39:42Z", "2021-06-25T14:39:41Z"], ["2021-06-15T02:50:37Z", "2021-06-16T02:50:36Z"], ["2021-05-12T00:52:56Z", "2021-05-13T00:52:55Z"], ["2021-05-04T03:55:39Z", "2021-05-05T03:55:38Z"]]);
    });
    it("creates correct intervals if spreadStartTime", function () {
      var _item$activeTableStyl4, _item$activeTableStyl5;
      runInAction(() => item.setTrait(CommonStrata.user, "defaultStyle", createStratumInstance(TableStyleTraits, {
        time: createStratumInstance(TableTimeStyleTraits, {
          spreadStartTime: true
        })
      })));
      expect(item.disableDateTimeSelector).toBeFalsy();
      expect((_item$activeTableStyl4 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl4 === void 0 ? void 0 : _item$activeTableStyl4.length).toBe(21);
      expect((_item$activeTableStyl5 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl5 === void 0 ? void 0 : _item$activeTableStyl5.map(t => [t === null || t === void 0 ? void 0 : t.start.toString(), t === null || t === void 0 ? void 0 : t.stop.toString()])).toEqual([["2021-05-04T03:55:39Z", "2021-06-26T10:39:01Z"], ["2021-05-04T03:55:39Z", "2021-06-26T10:26:44Z"], ["2021-05-04T03:55:39Z", "2021-06-26T10:18:00Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:53:51Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:51:31Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:47:05Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:19:20Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:14:35Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:06:46Z"], ["2021-05-04T03:55:39Z", "2021-06-26T09:01:31Z"], ["2021-05-04T03:55:39Z", "2021-06-26T08:25:08Z"], ["2021-05-04T03:55:39Z", "2021-06-26T07:22:14Z"], ["2021-05-04T03:55:39Z", "2021-06-26T06:10:51Z"], ["2021-05-04T03:55:39Z", "2021-06-26T04:39:44Z"], ["2021-05-04T03:55:39Z", "2021-06-26T03:46:12Z"], ["2021-05-04T03:55:39Z", "2021-06-26T00:29:25Z"], ["2021-05-04T03:55:39Z", "2021-06-26T00:27:22Z"], ["2021-05-04T03:55:39Z", "2021-06-25T14:39:41Z"], ["2021-05-04T03:55:39Z", "2021-06-16T02:50:36Z"], ["2021-05-04T03:55:39Z", "2021-05-13T00:52:55Z"], ["2021-05-04T03:55:39Z", "2021-05-05T03:55:38Z"]]);
    });
    it("creates correct intervals if spreadStartTime and spreadFinishTime", function () {
      var _item$activeTableStyl6;
      runInAction(() => item.setTrait(CommonStrata.user, "defaultStyle", createStratumInstance(TableStyleTraits, {
        time: createStratumInstance(TableTimeStyleTraits, {
          spreadStartTime: true,
          spreadFinishTime: true
        })
      })));
      expect(item.disableDateTimeSelector).toBeTruthy();
      expect((_item$activeTableStyl6 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl6 === void 0 ? void 0 : _item$activeTableStyl6.length).toBe(21);
      expect(item.activeTableStyle.moreThanOneTimeInterval).toBe(false);
    });
    it("creates correct intervals if spreadFinishTime", function () {
      var _item$activeTableStyl7, _item$activeTableStyl8;
      runInAction(() => item.setTrait(CommonStrata.user, "defaultStyle", createStratumInstance(TableStyleTraits, {
        time: createStratumInstance(TableTimeStyleTraits, {
          spreadFinishTime: true
        })
      })));
      expect((_item$activeTableStyl7 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl7 === void 0 ? void 0 : _item$activeTableStyl7.length).toBe(21);
      expect((_item$activeTableStyl8 = item.activeTableStyle.timeIntervals) === null || _item$activeTableStyl8 === void 0 ? void 0 : _item$activeTableStyl8.map(t => [t === null || t === void 0 ? void 0 : t.start.toString(), t === null || t === void 0 ? void 0 : t.stop.toString()])).toEqual([["2021-06-25T10:39:02Z", "2021-06-26T10:39:01Z"], ["2021-06-25T10:26:45Z", "2021-06-26T10:39:01Z"], ["2021-06-25T10:18:01Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:53:52Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:51:32Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:47:06Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:19:21Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:14:36Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:06:47Z", "2021-06-26T10:39:01Z"], ["2021-06-25T09:01:32Z", "2021-06-26T10:39:01Z"], ["2021-06-25T08:25:09Z", "2021-06-26T10:39:01Z"], ["2021-06-25T07:22:15Z", "2021-06-26T10:39:01Z"], ["2021-06-25T06:10:52Z", "2021-06-26T10:39:01Z"], ["2021-06-25T04:39:45Z", "2021-06-26T10:39:01Z"], ["2021-06-25T03:46:13Z", "2021-06-26T10:39:01Z"], ["2021-06-25T00:29:26Z", "2021-06-26T10:39:01Z"], ["2021-06-25T00:27:23Z", "2021-06-26T10:39:01Z"], ["2021-06-24T14:39:42Z", "2021-06-26T10:39:01Z"], ["2021-06-15T02:50:37Z", "2021-06-26T10:39:01Z"], ["2021-05-12T00:52:56Z", "2021-06-26T10:39:01Z"], ["2021-05-04T03:55:39Z", "2021-06-26T10:39:01Z"]]);
    });
    it("creates disable time dimension by default for this dataset", function () {
      expect(item.timeDisableDimension).toBeDefined();
    });
    it("doesn't disable time dimension if `showDisableTimeOption = false`", function () {
      runInAction(() => item.setTrait(CommonStrata.user, "showDisableTimeOption", false));
      expect(item.timeDisableDimension).toBeUndefined();
    });
    it("doesn't disable time dimension by default for another dataset", async function () {
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      });
      (await item.loadMapItems()).throwIfError();
      expect(item.timeDisableDimension).toBeUndefined();
    });
    it("creates disable time dimension for another dataset if `showDisableTimeOption = true`", async function () {
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
        item.setTrait(CommonStrata.user, "showDisableTimeOption", true);
      });
      (await item.loadMapItems()).throwIfError();
      expect(item.timeDisableDimension).toBeDefined();
    });
  });
  describe("when the table has a few styles", function () {
    it("creates all styleDimensions", async function () {
      var _item$styleDimensions, _item$styleDimensions2, _item$styleDimensions3;
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      });
      (await item.loadMapItems()).throwIfError();
      expect((_item$styleDimensions = item.styleDimensions) === null || _item$styleDimensions === void 0 || (_item$styleDimensions = _item$styleDimensions.options) === null || _item$styleDimensions === void 0 ? void 0 : _item$styleDimensions.length).toBe(4);
      expect((_item$styleDimensions2 = item.styleDimensions) === null || _item$styleDimensions2 === void 0 || (_item$styleDimensions2 = _item$styleDimensions2.options) === null || _item$styleDimensions2 === void 0 ? void 0 : _item$styleDimensions2[2].id).toBe("value");
      expect((_item$styleDimensions3 = item.styleDimensions) === null || _item$styleDimensions3 === void 0 || (_item$styleDimensions3 = _item$styleDimensions3.options) === null || _item$styleDimensions3 === void 0 ? void 0 : _item$styleDimensions3[2].name).toBe("Value");
    });
    it("creates all styleDimensions - with disable style", async function () {
      var _item$styleDimensions4, _item$styleDimensions5, _item$styleDimensions6;
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
        item.setTrait(CommonStrata.user, "showDisableStyleOption", true);
      });
      (await item.loadMapItems()).throwIfError();
      expect((_item$styleDimensions4 = item.styleDimensions) === null || _item$styleDimensions4 === void 0 || (_item$styleDimensions4 = _item$styleDimensions4.options) === null || _item$styleDimensions4 === void 0 ? void 0 : _item$styleDimensions4.length).toBe(4);
      expect((_item$styleDimensions5 = item.styleDimensions) === null || _item$styleDimensions5 === void 0 ? void 0 : _item$styleDimensions5.allowUndefined).toBeTruthy();
      expect((_item$styleDimensions6 = item.styleDimensions) === null || _item$styleDimensions6 === void 0 ? void 0 : _item$styleDimensions6.undefinedLabel).toBe("models.tableData.styleDisabledLabel");
    });
    it("uses TableColumnTraits for style title", async function () {
      var _item$styleDimensions7, _item$styleDimensions8;
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
        updateModelFromJson(item, CommonStrata.definition, {
          columns: [{
            name: "value",
            title: "Some Title"
          }]
        });
      });
      (await item.loadMapItems()).throwIfError();
      expect((_item$styleDimensions7 = item.styleDimensions) === null || _item$styleDimensions7 === void 0 || (_item$styleDimensions7 = _item$styleDimensions7.options) === null || _item$styleDimensions7 === void 0 ? void 0 : _item$styleDimensions7[2].id).toBe("value");
      expect((_item$styleDimensions8 = item.styleDimensions) === null || _item$styleDimensions8 === void 0 || (_item$styleDimensions8 = _item$styleDimensions8.options) === null || _item$styleDimensions8 === void 0 ? void 0 : _item$styleDimensions8[2].name).toBe("Some Title");
    });
    it("uses TableStyleTraits for style title", async function () {
      var _item$styleDimensions9, _item$styleDimensions0;
      runInAction(() => {
        item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
        updateModelFromJson(item, CommonStrata.definition, {
          columns: [{
            name: "value",
            title: "Some Title"
          }],
          styles: [{
            id: "value",
            title: "Some Style Title"
          }]
        });
      });
      (await item.loadMapItems()).throwIfError();
      expect((_item$styleDimensions9 = item.styleDimensions) === null || _item$styleDimensions9 === void 0 || (_item$styleDimensions9 = _item$styleDimensions9.options) === null || _item$styleDimensions9 === void 0 ? void 0 : _item$styleDimensions9[2].id).toBe("value");
      expect((_item$styleDimensions0 = item.styleDimensions) === null || _item$styleDimensions0 === void 0 || (_item$styleDimensions0 = _item$styleDimensions0.options) === null || _item$styleDimensions0 === void 0 ? void 0 : _item$styleDimensions0[2].name).toBe("Some Style Title");
    });
    it("loads regionProviderLists on loadMapItems", async function () {
      var _item$regionProviderL;
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      (await item.loadMetadata()).throwIfError();
      expect(item.regionProviderLists).toBeUndefined();
      (await item.loadMapItems()).throwIfError();
      expect((_item$regionProviderL = item.regionProviderLists) === null || _item$regionProviderL === void 0 || (_item$regionProviderL = _item$regionProviderL[0]) === null || _item$regionProviderL === void 0 ? void 0 : _item$regionProviderL.regionProviders.length).toBe(NUMBER_OF_REGION_MAPPING_TYPES);
    });
    it("loads regionProviderLists on loadMapItems - with multiple regionMappingDefinitionsUrl", async function () {
      var _item$regionProviderL2, _item$regionProviderL3, _item$regionProviderL4, _item$activeTableStyl9;
      // We add "additionalRegion.json" - which defines two region types
      // - "SOME_OTHER_REGION" - which is just another region type
      // - "SOME_OVERRIDDEN_REGION" - which will override "LGA_NAME_2011" in "build/TerriaJS/data/regionMapping.json"
      worker.use(http.get("*/additionalRegion.json", () => HttpResponse.json(additionalRegionMapping)), http.get("build/TerriaJS/data/regionids/region_map-FID_LGA_2011_AUST_LGA_NAME11.json", () => HttpResponse.json(regionIdsLgaNameStates)), http.get("build/TerriaJS/data/regionids/region_map-FID_LGA_2011_AUST_STE_NAME11.json", () => HttpResponse.json(regionIdsLgaName)));
      terria.updateParameters({
        regionMappingDefinitionsUrls: ["additionalRegion.json", "build/TerriaJS/data/regionMapping.json"]
      });
      item.setTrait(CommonStrata.user, "csvString", LgaWithDisambigCsv);
      (await item.loadMetadata()).throwIfError();
      expect(item.regionProviderLists).toBeUndefined();
      (await item.loadMapItems()).throwIfError();
      expect((_item$regionProviderL2 = item.regionProviderLists) === null || _item$regionProviderL2 === void 0 ? void 0 : _item$regionProviderL2.length).toBe(2);
      expect((_item$regionProviderL3 = item.regionProviderLists) === null || _item$regionProviderL3 === void 0 || (_item$regionProviderL3 = _item$regionProviderL3[0]) === null || _item$regionProviderL3 === void 0 ? void 0 : _item$regionProviderL3.regionProviders.length).toBe(2);
      expect((_item$regionProviderL4 = item.regionProviderLists) === null || _item$regionProviderL4 === void 0 || (_item$regionProviderL4 = _item$regionProviderL4[1]) === null || _item$regionProviderL4 === void 0 ? void 0 : _item$regionProviderL4.regionProviders.length).toBe(NUMBER_OF_REGION_MAPPING_TYPES);

      // Item region provider should match from "additionalRegion.json" (as it comes before "build/TerriaJS/data/regionMapping.json")
      expect((_item$activeTableStyl9 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl9 === void 0 || (_item$activeTableStyl9 = _item$activeTableStyl9.regionType) === null || _item$activeTableStyl9 === void 0 ? void 0 : _item$activeTableStyl9.description).toBe("Local Government Areas 2011 by name (ABS) !!!! OVERRIDDEN");
    });
    it("loads regionProviderLists on loadMapItems - will use regionMappingDefinitionsUrl instead of regionMappingDefinitionsUrls", async function () {
      var _item$regionProviderL5, _item$regionProviderL6, _item$activeTableStyl0;
      // We add "additionalRegion.json" - which defines two region types
      // - "SOME_OTHER_REGION" - which is just another region type
      // - "SOME_OVERRIDDEN_REGION" - which will override "LGA_NAME_2011" in "build/TerriaJS/data/regionMapping.json"
      worker.use(http.get("*/additionalRegion.json", () => HttpResponse.json(additionalRegionMapping)), http.get("build/TerriaJS/data/regionids/region_map-FID_LGA_2011_AUST_LGA_NAME11.json", () => HttpResponse.json(regionIdsLgaNameStates)), http.get("build/TerriaJS/data/regionids/region_map-FID_LGA_2011_AUST_STE_NAME11.json", () => HttpResponse.json(regionIdsLgaName)));
      terria.updateParameters({
        regionMappingDefinitionsUrl: "build/TerriaJS/data/regionMapping.json",
        regionMappingDefinitionsUrls: ["additionalRegion.json", "build/TerriaJS/data/regionMapping.json"]
      });
      item.setTrait(CommonStrata.user, "csvString", LgaWithDisambigCsv);
      (await item.loadMetadata()).throwIfError();
      expect(item.regionProviderLists).toBeUndefined();
      (await item.loadMapItems()).throwIfError();
      expect((_item$regionProviderL5 = item.regionProviderLists) === null || _item$regionProviderL5 === void 0 ? void 0 : _item$regionProviderL5.length).toBe(1);
      expect((_item$regionProviderL6 = item.regionProviderLists) === null || _item$regionProviderL6 === void 0 || (_item$regionProviderL6 = _item$regionProviderL6[0]) === null || _item$regionProviderL6 === void 0 ? void 0 : _item$regionProviderL6.regionProviders.length).toBe(NUMBER_OF_REGION_MAPPING_TYPES);

      // Item region provider should match from "build/TerriaJS/data/regionMapping.json"
      expect((_item$activeTableStyl0 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl0 === void 0 || (_item$activeTableStyl0 = _item$activeTableStyl0.regionType) === null || _item$activeTableStyl0 === void 0 ? void 0 : _item$activeTableStyl0.description).toBe("Local Government Areas 2011 by name (ABS)");
    });
  });
  describe("creates legend", function () {
    it(" - correct decimal places for values [0,100]", async function () {
      item.setTrait("definition", "csvString", LegendDecimalPlacesCsv);
      item.setTrait("definition", "activeStyle", "0dp");
      (await item.loadMapItems()).throwIfError();
      expect(item.legends[0].items.length).toBe(7);
      expect(item.legends[0].items.map(i => i.title)).toEqual(["65", "54", "44", "33", "22", "12", "1"]);
    });
    it(" - correct decimal places for values [0,10]", async function () {
      item.setTrait("definition", "csvString", LegendDecimalPlacesCsv);
      item.setTrait("definition", "activeStyle", "1dp");
      (await item.loadMapItems()).throwIfError();
      expect(item.legends[0].items.length).toBe(7);
      expect(item.legends[0].items.map(i => i.title)).toEqual(["10.0", "8.3", "6.7", "5.0", "3.3", "1.7", "0.0"]);
    });
    it(" - correct decimal places for values [0,1]", async function () {
      item.setTrait("definition", "csvString", LegendDecimalPlacesCsv);
      item.setTrait("definition", "activeStyle", "2dp");
      (await item.loadMapItems()).throwIfError();
      expect(item.legends[0].items.length).toBe(7);
      expect(item.legends[0].items.map(i => i.title)).toEqual(["0.70", "0.58", "0.47", "0.35", "0.23", "0.12", "0.00"]);
    });
    it(" - correct decimal places for values [0,0.1]", async function () {
      item.setTrait("definition", "csvString", LegendDecimalPlacesCsv);
      item.setTrait("definition", "activeStyle", "3dp");
      (await item.loadMapItems()).throwIfError();
      expect(item.legends[0].items.length).toBe(7);
      expect(item.legends[0].items.map(i => i.title)).toEqual(["0.080", "0.068", "0.057", "0.045", "0.033", "0.022", "0.010"]);
    });
    it(" - uses color column title by default", async function () {
      item.setTrait("definition", "csvString", LegendDecimalPlacesCsv);
      item.setTrait("definition", "activeStyle", "0dp");
      updateModelFromJson(item, CommonStrata.user, {
        styles: [{
          name: "0dp",
          title: "Some title"
        }]
      });
      (await item.loadMapItems()).throwIfError();
      expect(item.legends[0].title).toBe("0dp");
    });
  });
  describe("region mapping - LGA with disambig", function () {
    beforeEach(async function () {
      var _item$regionProviderL7, _item$regionProviderL8;
      item.setTrait(CommonStrata.user, "csvString", LgaWithDisambigCsv);
      (await item.loadMapItems()).throwIfError();
      await ((_item$regionProviderL7 = item.regionProviderLists) === null || _item$regionProviderL7 === void 0 || (_item$regionProviderL7 = _item$regionProviderL7[0]) === null || _item$regionProviderL7 === void 0 || (_item$regionProviderL7 = _item$regionProviderL7.getRegionProvider("LGA_NAME_2011")) === null || _item$regionProviderL7 === void 0 ? void 0 : _item$regionProviderL7.loadRegionIDs());
      await ((_item$regionProviderL8 = item.regionProviderLists) === null || _item$regionProviderL8 === void 0 || (_item$regionProviderL8 = _item$regionProviderL8[0]) === null || _item$regionProviderL8 === void 0 || (_item$regionProviderL8 = _item$regionProviderL8.getRegionProvider("STE_NAME_2016")) === null || _item$regionProviderL8 === void 0 ? void 0 : _item$regionProviderL8.loadRegionIDs());
    });
    it("creates imagery parts", function () {
      expect(ImageryParts.is(item.mapItems[0])).toBeTruthy();
    });
    it("with state", function () {
      var _item$activeTableStyl1, _item$activeTableStyl10, _item$activeTableStyl11, _item$activeTableStyl12;
      updateModelFromJson(item, CommonStrata.user, {
        columns: [{
          name: "State",
          regionType: "STE_NAME_2016"
        }],
        defaultStyle: {
          regionColumn: "State"
        }
      });
      expect((_item$activeTableStyl1 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl1 === void 0 ? void 0 : _item$activeTableStyl1.name).toBe("State");
      expect((_item$activeTableStyl10 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl10 === void 0 || (_item$activeTableStyl10 = _item$activeTableStyl10.regionType) === null || _item$activeTableStyl10 === void 0 ? void 0 : _item$activeTableStyl10.regionType).toBe("STE_NAME_2016");
      expect((_item$activeTableStyl11 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl11 === void 0 ? void 0 : _item$activeTableStyl11.valuesAsRegions.numberOfValidRegions).toBe(8);
      expect((_item$activeTableStyl12 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl12 === void 0 ? void 0 : _item$activeTableStyl12.valuesAsRegions.uniqueRegionIds.length).toBe(3);
    });
    it("with lga_name", function () {
      var _item$activeTableStyl13, _item$activeTableStyl14, _item$activeTableStyl15, _item$activeTableStyl16;
      updateModelFromJson(item, CommonStrata.user, {
        columns: [{
          name: "LGA_NAME",
          regionType: "LGA_NAME_2011"
        }],
        defaultStyle: {
          regionColumn: "LGA_NAME"
        }
      });
      expect((_item$activeTableStyl13 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl13 === void 0 ? void 0 : _item$activeTableStyl13.name).toBe("LGA_NAME");
      expect((_item$activeTableStyl14 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl14 === void 0 || (_item$activeTableStyl14 = _item$activeTableStyl14.regionType) === null || _item$activeTableStyl14 === void 0 ? void 0 : _item$activeTableStyl14.regionType).toBe("LGA_NAME_2011");
      expect((_item$activeTableStyl15 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl15 === void 0 ? void 0 : _item$activeTableStyl15.valuesAsRegions.numberOfValidRegions).toBe(8);
      expect((_item$activeTableStyl16 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl16 === void 0 ? void 0 : _item$activeTableStyl16.valuesAsRegions.uniqueRegionIds.length).toBe(8);
    });
    it("matches column name with whitespace", async function () {
      var _item$activeTableStyl17, _item$activeTableStyl18;
      item.setTrait(CommonStrata.user, "csvString", "lga code-_-2015,number\n        35740,1\n        36720,2\n        ");
      (await item.loadMapItems()).throwIfError();
      expect((_item$activeTableStyl17 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl17 === void 0 ? void 0 : _item$activeTableStyl17.name).toBe("lga code-_-2015");
      expect((_item$activeTableStyl18 = item.activeTableStyle.regionColumn) === null || _item$activeTableStyl18 === void 0 || (_item$activeTableStyl18 = _item$activeTableStyl18.regionType) === null || _item$activeTableStyl18 === void 0 ? void 0 : _item$activeTableStyl18.regionType).toBe("LGA_2015");
    });
    it("shows region shortReportSection", function () {
      const regionCol = item.activeTableStyle.regionColumn;
      const regionType = regionCol === null || regionCol === void 0 ? void 0 : regionCol.regionType;
      expect(regionType).toBeDefined();
      expect(item.shortReportSections[0].name).toBe("**Regions:** ".concat(regionType === null || regionType === void 0 ? void 0 : regionType.description));
    });
    it("doesn't show region shortReportSection if region is disabled", function () {
      updateModelFromJson(item, CommonStrata.user, {
        defaultStyle: {
          regionColumn: "Something else"
        }
      });
      expect(item.shortReportSections.length).toBe(0);
    });
  });
  describe("applies TableStyles to lat/lon features", function () {
    it("supports image marker style - data URI", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      const image = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IB2cksfwAAAAlwSFlzAAAOxAAADsQBlSsOGwAAAZNJREFUWIXtlrFLQlEUxr/QPGYhTbYEhhgWErjU0twQ9AdEGNnU0CiBL60pMBqC2gSHhgoamhoFt8ZaIugtQhFFY1Hmp0EN+ehZDd3e84ngB493733vnPPjXj7OdaPFcncA2h3AIyLrJBMiskcy01QAEdkgmTavkTTeaQDpbyFdtgKQXFH5/y9SPQKPefII7PuB2Xqe0xGRgE4ONxOgQX4gbppOHpGlmGIOW10wCoRUY2wFKP0jxhJAHMAqgCCAawBRpwEO6o8VWQKYjqK8lYAEA3Bd3UGf0BBxFOBYg6+n+3M8HkJkaQqVXAFexwCM4oZ2F+HNFdRytHczeq017sJyHi8Aeh0DmMniaSeB96EBeC5ucJ4vYlI1hyWA4iX8Y1/tSbm4ZQCJohxegHgDcD3fQtczDtswtgafMe4PIxLZRElPqfUDW13QN9jiZlR5UI+xBPBWA9wmG96fNN+GVZhuRWfzP74rFVcGEJFtkinVIrYBkNQAaKYlj4hkSc6JyCHJZFMBflG1XjRpXM+dBrCsDsAHoPtrwlSQt8wAAAAASUVORK5CYII=";
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        point: createStratumInstance(TablePointStyleTraits, {
          null: createStratumInstance(PointSymbolTraits, {
            marker: image,
            height: 20
          })
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      mapItem.entities.values.forEach(feature => {
        var _feature$billboard, _feature$billboard2;
        expect((_feature$billboard = feature.billboard) === null || _feature$billboard === void 0 || (_feature$billboard = _feature$billboard.image) === null || _feature$billboard === void 0 ? void 0 : _feature$billboard.getValue(item.terria.timelineClock.currentTime)).toBe(image);
        expect((_feature$billboard2 = feature.billboard) === null || _feature$billboard2 === void 0 || (_feature$billboard2 = _feature$billboard2.height) === null || _feature$billboard2 === void 0 ? void 0 : _feature$billboard2.getValue(item.terria.timelineClock.currentTime)).toBe(20);
      });
    });
    it("supports image marker style - URL", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      const image = "http://localhost:3001/build/TerriaJS/images/map-pin.svg";
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        point: createStratumInstance(TablePointStyleTraits, {
          null: createStratumInstance(PointSymbolTraits, {
            marker: image,
            height: 20
          })
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      mapItem.entities.values.forEach(feature => {
        var _feature$billboard3, _feature$billboard4;
        expect((_feature$billboard3 = feature.billboard) === null || _feature$billboard3 === void 0 || (_feature$billboard3 = _feature$billboard3.image) === null || _feature$billboard3 === void 0 ? void 0 : _feature$billboard3.getValue(item.terria.timelineClock.currentTime)).toBe(image);
        expect((_feature$billboard4 = feature.billboard) === null || _feature$billboard4 === void 0 || (_feature$billboard4 = _feature$billboard4.height) === null || _feature$billboard4 === void 0 ? void 0 : _feature$billboard4.getValue(item.terria.timelineClock.currentTime)).toBe(20);
      });
    });
    it("bin outline style with points", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          colorColumn: "value",
          colorPalette: "Greens",
          numberOfBins: 7
        }),
        point: createStratumInstance(TablePointStyleTraits, {
          column: "value",
          bin: [createStratumInstance(BinPointSymbolTraits, {
            maxValue: 1,
            marker: "point",
            height: 20
          }), createStratumInstance(BinPointSymbolTraits, {
            maxValue: 3,
            marker: "point",
            height: 10
          }), createStratumInstance(BinPointSymbolTraits, {
            maxValue: 5,
            marker: "point",
            height: 30
          })]
        }),
        outline: createStratumInstance(TableOutlineStyleTraits, {
          column: "value",
          bin: [createStratumInstance(BinOutlineSymbolTraits, {
            maxValue: 1,
            color: "rgb(0,0,0)",
            width: 1
          }), createStratumInstance(BinOutlineSymbolTraits, {
            maxValue: 3,
            color: "rgb(255,0,0)",
            width: 2
          }), createStratumInstance(BinOutlineSymbolTraits, {
            maxValue: 5,
            color: "rgb(0,255,0)",
            width: 3
          })]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      const styles = [{
        fillColor: "rgb(35,139,69)",
        outlineColor: "rgb(0,255,0)",
        outlineWidth: 3,
        pixelSize: 30
      }, {
        fillColor: "rgb(116,196,118)",
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelSize: 10
      }, {
        fillColor: "rgb(186,228,179)",
        outlineColor: "rgb(0,0,0)",
        outlineWidth: 1,
        pixelSize: 20
      }, {
        fillColor: "rgb(237,248,233)",
        outlineColor: "rgb(0,0,0)",
        outlineWidth: 1,
        pixelSize: 20
      }, {
        fillColor: "rgb(116,196,118)",
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelSize: 10
      }];
      styles.forEach((style, index) => {
        var _feature$point, _feature$point2, _feature$point3, _feature$point4;
        const feature = mapItem.entities.values[index];
        expect((_feature$point = feature.point) === null || _feature$point === void 0 || (_feature$point = _feature$point.color) === null || _feature$point === void 0 || (_feature$point = _feature$point.getValue(item.terria.timelineClock.currentTime)) === null || _feature$point === void 0 ? void 0 : _feature$point.toCssColorString()).toBe(style.fillColor);
        expect((_feature$point2 = feature.point) === null || _feature$point2 === void 0 || (_feature$point2 = _feature$point2.outlineColor) === null || _feature$point2 === void 0 || (_feature$point2 = _feature$point2.getValue(item.terria.timelineClock.currentTime)) === null || _feature$point2 === void 0 ? void 0 : _feature$point2.toCssColorString()).toBe(style.outlineColor);
        expect((_feature$point3 = feature.point) === null || _feature$point3 === void 0 || (_feature$point3 = _feature$point3.outlineWidth) === null || _feature$point3 === void 0 ? void 0 : _feature$point3.getValue(item.terria.timelineClock.currentTime)).toBe(style.outlineWidth);
        expect((_feature$point4 = feature.point) === null || _feature$point4 === void 0 || (_feature$point4 = _feature$point4.pixelSize) === null || _feature$point4 === void 0 ? void 0 : _feature$point4.getValue(item.terria.timelineClock.currentTime)).toBe(style.pixelSize);
      });
    });
    it("bin color and outline style with markers", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(0,255,255)"
        }),
        point: createStratumInstance(TablePointStyleTraits, {
          column: "value",
          bin: [createStratumInstance(BinPointSymbolTraits, {
            maxValue: 1,
            marker: "circle",
            height: 20,
            width: 10
          }), createStratumInstance(BinPointSymbolTraits, {
            maxValue: 3,
            marker: "cross",
            height: 10,
            width: 5
          }), createStratumInstance(BinPointSymbolTraits, {
            maxValue: 5,
            marker: "hospital",
            height: 30,
            width: 15,
            rotation: 45
          })]
        }),
        outline: createStratumInstance(TableOutlineStyleTraits, {
          null: createStratumInstance(BinOutlineSymbolTraits, {
            maxValue: 1,
            color: "rgb(0,0,255)",
            width: 1
          })
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      const styles = [{
        fillColor: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "hospital",
        height: 30,
        width: 15,
        // Convert to counter-clockwise radians
        rotation: (360 - 45) / 360 * (2 * Math.PI)
      }, {
        fillColor: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "cross",
        height: 10,
        width: 5,
        rotation: 2 * Math.PI
      }, {
        fillColor: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "circle",
        height: 20,
        width: 10,
        rotation: 2 * Math.PI
      }, {
        fillColor: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "circle",
        height: 20,
        width: 10,
        rotation: 2 * Math.PI
      }, {
        fillColor: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "cross",
        height: 10,
        width: 5,
        rotation: 2 * Math.PI
      }];
      styles.forEach((style, index) => {
        var _feature$billboard5, _feature$billboard6;
        const feature = mapItem.entities.values[index];
        expect((_feature$billboard5 = feature.billboard) === null || _feature$billboard5 === void 0 || (_feature$billboard5 = _feature$billboard5.rotation) === null || _feature$billboard5 === void 0 ? void 0 : _feature$billboard5.getValue(item.terria.timelineClock.currentTime)).toBeCloseTo(style.rotation);
        expect((_feature$billboard6 = feature.billboard) === null || _feature$billboard6 === void 0 || (_feature$billboard6 = _feature$billboard6.image) === null || _feature$billboard6 === void 0 ? void 0 : _feature$billboard6.getValue(item.terria.timelineClock.currentTime)).toBe(getMakiIcon(style.marker, style.fillColor, style.outlineWidth, style.outlineColor, style.height, style.width));
      });

      // Test merging legends

      expect(item.legends[0].items.map(item => ({
        title: item.title,
        outlineColor: item.outlineColor,
        outlineWidth: item.outlineWidth,
        imageHeight: item.imageHeight,
        imageWidth: item.imageWidth,
        color: item.color,
        marker: item.marker,
        rotation: item.rotation
      }))).toEqual([{
        title: "3 to 5",
        color: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "hospital",
        rotation: 45,
        imageHeight: 24,
        imageWidth: 24
      }, {
        title: "1 to 3",
        color: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "cross",
        imageHeight: 24,
        imageWidth: 24,
        rotation: 0
      }, {
        title: "-1 to 1",
        color: "rgb(0,255,255)",
        outlineColor: "rgb(0,0,255)",
        outlineWidth: 1,
        marker: "circle",
        imageHeight: 24,
        imageWidth: 24,
        rotation: 0
      }]);
    });
    it("enum outline style with points", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(255,0,255)"
        }),
        point: createStratumInstance(TablePointStyleTraits, {
          column: "enum",
          enum: [createStratumInstance(EnumPointSymbolTraits, {
            value: "hello",
            height: 20
          }), createStratumInstance(EnumPointSymbolTraits, {
            value: "boots",
            height: 10
          }), createStratumInstance(EnumPointSymbolTraits, {
            value: "frogs",
            height: 30
          })]
        }),
        outline: createStratumInstance(TableOutlineStyleTraits, {
          column: "enum",
          enum: [createStratumInstance(EnumOutlineSymbolTraits, {
            value: "hello",
            color: "rgb(0,0,0)",
            width: 1
          }), createStratumInstance(EnumOutlineSymbolTraits, {
            value: "boots",
            color: "rgb(255,0,0)",
            width: 2
          }), createStratumInstance(EnumOutlineSymbolTraits, {
            value: "frogs",
            color: "rgb(0,255,0)",
            width: 3
          })]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      const styles = [{
        fillColor: "rgb(255,0,255)",
        outlineColor: "rgb(0,0,0)",
        outlineWidth: 1,
        pixelSize: 20
      }, {
        fillColor: "rgb(255,0,255)",
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelSize: 10
      }, {
        fillColor: "rgb(255,0,255)",
        outlineColor: "rgb(0,255,0)",
        outlineWidth: 3,
        pixelSize: 30
      }, {
        fillColor: "rgb(255,0,255)",
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelSize: 10
      }, {
        fillColor: "rgb(255,0,255)",
        outlineColor: "rgb(0,0,0)",
        outlineWidth: 1,
        pixelSize: 20
      }];
      styles.forEach((style, index) => {
        var _feature$point5, _feature$point6, _feature$point7, _feature$point8;
        const feature = mapItem.entities.values[index];
        expect((_feature$point5 = feature.point) === null || _feature$point5 === void 0 || (_feature$point5 = _feature$point5.color) === null || _feature$point5 === void 0 || (_feature$point5 = _feature$point5.getValue(item.terria.timelineClock.currentTime)) === null || _feature$point5 === void 0 ? void 0 : _feature$point5.toCssColorString()).toBe(style.fillColor);
        expect((_feature$point6 = feature.point) === null || _feature$point6 === void 0 || (_feature$point6 = _feature$point6.outlineColor) === null || _feature$point6 === void 0 || (_feature$point6 = _feature$point6.getValue(item.terria.timelineClock.currentTime)) === null || _feature$point6 === void 0 ? void 0 : _feature$point6.toCssColorString()).toBe(style.outlineColor);
        expect((_feature$point7 = feature.point) === null || _feature$point7 === void 0 || (_feature$point7 = _feature$point7.outlineWidth) === null || _feature$point7 === void 0 ? void 0 : _feature$point7.getValue(item.terria.timelineClock.currentTime)).toBe(style.outlineWidth);
        expect((_feature$point8 = feature.point) === null || _feature$point8 === void 0 || (_feature$point8 = _feature$point8.pixelSize) === null || _feature$point8 === void 0 ? void 0 : _feature$point8.getValue(item.terria.timelineClock.currentTime)).toBe(style.pixelSize);

        // Test merging legends

        expect(item.legends[0].items.map(item => ({
          title: item.title,
          outlineColor: item.outlineColor,
          outlineWidth: item.outlineWidth,
          imageHeight: item.imageHeight,
          imageWidth: item.imageWidth
        }))).toEqual([{
          title: "hello",
          outlineColor: "rgb(0,0,0)",
          outlineWidth: 1,
          imageHeight: 24,
          imageWidth: 24
        }, {
          title: "boots",
          outlineColor: "rgb(255,0,0)",
          outlineWidth: 2,
          imageHeight: 24,
          imageWidth: 24
        }, {
          title: "frogs",
          outlineColor: "rgb(0,255,0)",
          outlineWidth: 3,
          imageHeight: 24,
          imageWidth: 24
        }]);
      });
    });
    it("enum label style with points", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(255,0,255)"
        }),
        label: createStratumInstance(TableLabelStyleTraits, {
          column: "enum",
          enabled: true,
          enum: [createStratumInstance(EnumLabelSymbolTraits, {
            value: "hello",
            labelColumn: "enum",
            font: "10px sans-serif",
            style: "FILL",
            scale: 1.5,
            fillColor: "#ff00ff",
            pixelOffset: [0, 0],
            horizontalOrigin: "LEFT",
            verticalOrigin: "TOP"
          }), createStratumInstance(EnumLabelSymbolTraits, {
            value: "boots",
            labelColumn: "enum",
            font: "20px Arial",
            style: "FILL_AND_OUTLINE",
            scale: 2,
            fillColor: "#0000ff",
            outlineColor: "#00ff00",
            outlineWidth: 1,
            pixelOffset: [1, 1],
            horizontalOrigin: "CENTER",
            verticalOrigin: "CENTER"
          }), createStratumInstance(EnumLabelSymbolTraits, {
            value: "frogs",
            labelColumn: "lon",
            font: "30px serif",
            style: "OUTLINE",
            scale: 3,
            outlineColor: "#ff0000",
            outlineWidth: 2,
            pixelOffset: [2, 2],
            horizontalOrigin: "RIGHT",
            verticalOrigin: "BOTTOM"
          })]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];

      // Note these are slightly different from EnumLabelSymbolTraits above
      // This is because some values get converted into cesium formats (eg Cartesian2, LabelStyle etc)
      const enumStyles = [{
        value: "hello",
        text: "hello",
        // labelColumn: "enum",
        labelColumn: "enum",
        font: "10px sans-serif",
        style: LabelStyle.FILL,
        scale: 1.5,
        fillColor: "rgb(255,0,255)",
        pixelOffset: "(0, 0)",
        // Cartesian2.toString()
        horizontalOrigin: HorizontalOrigin.LEFT,
        verticalOrigin: VerticalOrigin.TOP
      }, {
        value: "boots",
        text: "boots",
        //labelColumn: "enum",
        font: "20px Arial",
        style: LabelStyle.FILL_AND_OUTLINE,
        scale: 2,
        fillColor: "rgb(0,0,255)",
        outlineColor: "rgb(0,255,0)",
        outlineWidth: 1,
        pixelOffset: "(1, 1)",
        // Cartesian2.toString()
        horizontalOrigin: HorizontalOrigin.CENTER,
        verticalOrigin: VerticalOrigin.CENTER
      }, {
        value: "frogs",
        text: "145",
        // labelColumn: "lon",
        font: "30px serif",
        style: LabelStyle.OUTLINE,
        scale: 3,
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelOffset: "(2, 2)",
        // Cartesian2.toString()
        horizontalOrigin: HorizontalOrigin.RIGHT,
        verticalOrigin: VerticalOrigin.BOTTOM
      }];
      mapItem.entities.values.forEach(feature => {
        var _feature$label, _feature$label2, _feature$label3, _feature$label4, _feature$label5, _feature$label6, _feature$label7, _feature$label8, _feature$label9, _feature$label0;
        // Find corresponding style by checking `enum` property
        const style = enumStyles.find(s => {
          var _feature$properties;
          return s.value === ((_feature$properties = feature.properties) === null || _feature$properties === void 0 ? void 0 : _feature$properties.getValue(item.terria.timelineClock.currentTime).enum);
        });
        if (!style) throw "Invalid feature styling!";
        const failMessage = prop => "Failed to test feature ID: ".concat(feature.id, ", ID value: ").concat(style.value, ", property: ").concat(prop);
        expect((_feature$label = feature.label) === null || _feature$label === void 0 || (_feature$label = _feature$label.text) === null || _feature$label === void 0 ? void 0 : _feature$label.getValue(item.terria.timelineClock.currentTime)).toBe(style.text, failMessage("text"));
        expect((_feature$label2 = feature.label) === null || _feature$label2 === void 0 || (_feature$label2 = _feature$label2.font) === null || _feature$label2 === void 0 ? void 0 : _feature$label2.getValue(item.terria.timelineClock.currentTime)).toBe(style.font, failMessage("font"));
        expect((_feature$label3 = feature.label) === null || _feature$label3 === void 0 || (_feature$label3 = _feature$label3.style) === null || _feature$label3 === void 0 ? void 0 : _feature$label3.getValue(item.terria.timelineClock.currentTime)).toBe(style.style, failMessage("style"));
        expect((_feature$label4 = feature.label) === null || _feature$label4 === void 0 || (_feature$label4 = _feature$label4.scale) === null || _feature$label4 === void 0 ? void 0 : _feature$label4.getValue(item.terria.timelineClock.currentTime)).toBe(style.scale, failMessage("scale"));
        expect((_feature$label5 = feature.label) === null || _feature$label5 === void 0 || (_feature$label5 = _feature$label5.pixelOffset) === null || _feature$label5 === void 0 || (_feature$label5 = _feature$label5.getValue(item.terria.timelineClock.currentTime)) === null || _feature$label5 === void 0 ? void 0 : _feature$label5.toString()).toBe(style.pixelOffset, failMessage("pixelOffset"));
        expect((_feature$label6 = feature.label) === null || _feature$label6 === void 0 || (_feature$label6 = _feature$label6.verticalOrigin) === null || _feature$label6 === void 0 ? void 0 : _feature$label6.getValue(item.terria.timelineClock.currentTime)).toBe(style.verticalOrigin, failMessage("verticalOrigin"));
        expect((_feature$label7 = feature.label) === null || _feature$label7 === void 0 || (_feature$label7 = _feature$label7.horizontalOrigin) === null || _feature$label7 === void 0 ? void 0 : _feature$label7.getValue(item.terria.timelineClock.currentTime)).toBe(style.horizontalOrigin, failMessage("horizontalOrigin"));
        if (style.fillColor) expect((_feature$label8 = feature.label) === null || _feature$label8 === void 0 || (_feature$label8 = _feature$label8.fillColor) === null || _feature$label8 === void 0 || (_feature$label8 = _feature$label8.getValue(item.terria.timelineClock.currentTime)) === null || _feature$label8 === void 0 ? void 0 : _feature$label8.toCssColorString()).toBe(style.fillColor, failMessage("fillColor"));
        if (style.outlineColor) expect((_feature$label9 = feature.label) === null || _feature$label9 === void 0 || (_feature$label9 = _feature$label9.outlineColor) === null || _feature$label9 === void 0 || (_feature$label9 = _feature$label9.getValue(item.terria.timelineClock.currentTime)) === null || _feature$label9 === void 0 ? void 0 : _feature$label9.toCssColorString()).toBe(style.outlineColor, failMessage("outlineColor"));
        if (style.outlineWidth) expect((_feature$label0 = feature.label) === null || _feature$label0 === void 0 || (_feature$label0 = _feature$label0.outlineWidth) === null || _feature$label0 === void 0 ? void 0 : _feature$label0.getValue(item.terria.timelineClock.currentTime)).toBe(style.outlineWidth, failMessage("outlineWidth"));
      });
    });
    it("enum label style style with time-enabled points - static styling", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(255,0,255)"
        }),
        label: createStratumInstance(TableLabelStyleTraits, {
          column: "id",
          enabled: true,
          null: createStratumInstance(LabelSymbolTraits, {
            labelColumn: "id",
            font: "40px serif",
            style: "OUTLINE",
            scale: 4,
            outlineColor: "#ffffff",
            outlineWidth: 3,
            pixelOffset: [3, 3]
          }),
          enum: [createStratumInstance(EnumLabelSymbolTraits, {
            value: "feature A",
            labelColumn: "id",
            font: "10px sans-serif",
            style: "FILL",
            scale: 1.5,
            fillColor: "#ff00ff",
            pixelOffset: [0, 0]
          }), createStratumInstance(EnumLabelSymbolTraits, {
            value: "feature B",
            labelColumn: "id",
            font: "20px Arial",
            style: "FILL_AND_OUTLINE",
            scale: 2,
            fillColor: "#0000ff",
            outlineColor: "#00ff00",
            outlineWidth: 1,
            pixelOffset: [1, 1]
          }), createStratumInstance(EnumLabelSymbolTraits, {
            value: "feature C",
            labelColumn: "id",
            font: "30px serif",
            style: "OUTLINE",
            scale: 3,
            outlineColor: "#ff0000",
            outlineWidth: 2,
            pixelOffset: [2, 2]
          })]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];

      // Note these are slightly different from EnumLabelSymbolTraits above
      // This is because some values get converted into cesium formats (eg Cartesian2, LabelStyle etc)
      const enumStyles = [{
        value: "feature A",
        text: "feature A",
        labelColumn: "enum",
        font: "10px sans-serif",
        style: LabelStyle.FILL,
        scale: 1.5,
        fillColor: "rgb(255,0,255)",
        pixelOffset: "(0, 0)" // Cartesian2.toString()
      }, {
        value: "feature B",
        text: "feature B",
        font: "20px Arial",
        style: LabelStyle.FILL_AND_OUTLINE,
        scale: 2,
        fillColor: "rgb(0,0,255)",
        outlineColor: "rgb(0,255,0)",
        outlineWidth: 1,
        pixelOffset: "(1, 1)" // Cartesian2.toString()
      }, {
        value: "feature C",
        text: "feature C",
        font: "30px serif",
        style: LabelStyle.OUTLINE,
        scale: 3,
        outlineColor: "rgb(255,0,0)",
        outlineWidth: 2,
        pixelOffset: "(2, 2)" // Cartesian2.toString()
      }];
      const nullStyle = {
        value: undefined,
        text: "feature D",
        // null style only applied to "feature D"
        font: "40px serif",
        style: LabelStyle.OUTLINE,
        scale: 4,
        fillColor: undefined,
        outlineColor: "rgb(255,255,255)",
        outlineWidth: 3,
        pixelOffset: "(3, 3)" // Cartesian2.toString()
      };
      mapItem.entities.values.forEach(feature => {
        var _item$discreteTimesAs;
        // We check all discrete times - but all values are the same for every timestamp
        (_item$discreteTimesAs = item.discreteTimesAsSortedJulianDates) === null || _item$discreteTimesAs === void 0 || _item$discreteTimesAs.map(discreteTime => {
          var _feature$position, _featureData$getValue, _enumStyles$find, _feature$label1, _feature$label10, _feature$label11, _feature$label12, _feature$label13, _feature$label14, _feature$label15, _feature$label16;
          // Don't bother checking point features with no position property
          if (!((_feature$position = feature.position) !== null && _feature$position !== void 0 && _feature$position.getValue(discreteTime.time))) return;

          // Find corresponding style by checking `enum` property
          const featureData = feature.data.timeIntervalCollection;
          if (!featureData)
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            "Failed to find featureData for feature ID: ".concat(feature.id);

          // If feature data doesn't contain current discrete time - we can safely continue
          if (!(featureData !== null && featureData !== void 0 && featureData.intervals.contains(discreteTime.time))) return;
          const featureIdValue = featureData === null || featureData === void 0 || (_featureData$getValue = featureData.getValue(discreteTime.time)) === null || _featureData$getValue === void 0 ? void 0 : _featureData$getValue.id;
          const style = (_enumStyles$find = enumStyles.find(s => s.value === featureIdValue)) !== null && _enumStyles$find !== void 0 ? _enumStyles$find : nullStyle;
          const failMessage = prop => "Failed to test feature ID: ".concat(feature.id, ", ID value: ").concat(style.value, ", time: ").concat(discreteTime.tag, ", property: ").concat(prop);
          expect((_feature$label1 = feature.label) === null || _feature$label1 === void 0 || (_feature$label1 = _feature$label1.text) === null || _feature$label1 === void 0 ? void 0 : _feature$label1.getValue(discreteTime.time)).toBe(style.text, failMessage("text"));
          expect((_feature$label10 = feature.label) === null || _feature$label10 === void 0 || (_feature$label10 = _feature$label10.font) === null || _feature$label10 === void 0 ? void 0 : _feature$label10.getValue(discreteTime.time)).toBe(style.font, failMessage("font"));
          expect((_feature$label11 = feature.label) === null || _feature$label11 === void 0 || (_feature$label11 = _feature$label11.style) === null || _feature$label11 === void 0 ? void 0 : _feature$label11.getValue(discreteTime.time)).toBe(style.style, failMessage("style"));
          expect((_feature$label12 = feature.label) === null || _feature$label12 === void 0 || (_feature$label12 = _feature$label12.scale) === null || _feature$label12 === void 0 ? void 0 : _feature$label12.getValue(discreteTime.time)).toBe(style.scale, failMessage("scale"));
          expect((_feature$label13 = feature.label) === null || _feature$label13 === void 0 || (_feature$label13 = _feature$label13.pixelOffset) === null || _feature$label13 === void 0 || (_feature$label13 = _feature$label13.getValue(discreteTime.time)) === null || _feature$label13 === void 0 ? void 0 : _feature$label13.toString()).toBe(style.pixelOffset, failMessage("pixelOffset"));
          if (style.fillColor) expect((_feature$label14 = feature.label) === null || _feature$label14 === void 0 || (_feature$label14 = _feature$label14.fillColor) === null || _feature$label14 === void 0 || (_feature$label14 = _feature$label14.getValue(discreteTime.time)) === null || _feature$label14 === void 0 ? void 0 : _feature$label14.toCssColorString()).toBe(style.fillColor, failMessage("fillColor"));
          if (style.outlineColor) expect((_feature$label15 = feature.label) === null || _feature$label15 === void 0 || (_feature$label15 = _feature$label15.outlineColor) === null || _feature$label15 === void 0 || (_feature$label15 = _feature$label15.getValue(discreteTime.time)) === null || _feature$label15 === void 0 ? void 0 : _feature$label15.toCssColorString()).toBe(style.outlineColor, failMessage("outlineColor"));
          if (style.outlineWidth) expect((_feature$label16 = feature.label) === null || _feature$label16 === void 0 || (_feature$label16 = _feature$label16.outlineWidth) === null || _feature$label16 === void 0 ? void 0 : _feature$label16.getValue(discreteTime.time)).toBe(style.outlineWidth, failMessage("outlineWidth"));
        });
      });
    });
    it("enum trail/path style with time-enabled points - static styling", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      const enumStyles = [{
        value: "feature A",
        leadTime: 0,
        trailTime: 10,
        width: 1,
        resolution: 40,
        polylineGlow: {
          color: "rgb(255,0,0)",
          glowPower: 0.1,
          taperPower: 0.4
        }
      }, {
        value: "feature B",
        leadTime: 10,
        trailTime: 20,
        width: 1,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,255,0)",
          glowPower: 0.2,
          taperPower: 0.5
        }
      }, {
        value: "feature C",
        leadTime: 20,
        trailTime: 60,
        width: 3,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,0,255)",
          glowPower: 0.3,
          taperPower: 0.6
        }
      }, {
        value: "feature D",
        leadTime: 20,
        trailTime: 60,
        width: 3,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,0,255)",
          glowPower: 0.3,
          taperPower: 0.6
        }
      }];
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(255,0,255)"
        }),
        trail: createStratumInstance(TableTrailStyleTraits, {
          column: "id",
          enabled: true,
          materialType: "polylineGlow",
          enum: [createStratumInstance(EnumTrailSymbolTraits, enumStyles[0]), createStratumInstance(EnumTrailSymbolTraits, enumStyles[1]), createStratumInstance(EnumTrailSymbolTraits, enumStyles[2]), createStratumInstance(EnumTrailSymbolTraits, enumStyles[3])]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];
      mapItem.entities.values.forEach(feature => {
        var _item$discreteTimesAs2;
        // We check all discrete times - but all values are the same for every timestamp
        (_item$discreteTimesAs2 = item.discreteTimesAsSortedJulianDates) === null || _item$discreteTimesAs2 === void 0 || _item$discreteTimesAs2.map(discreteTime => {
          var _feature$position2, _featureData$getValue2, _feature$path, _feature$path2, _feature$path3, _feature$path4, _feature$path5;
          // Don't bother checking point features with no position property
          if (!((_feature$position2 = feature.position) !== null && _feature$position2 !== void 0 && _feature$position2.getValue(discreteTime.time))) return;

          // Find corresponding style by checking `enum` property
          const featureData = feature.data.timeIntervalCollection;
          if (!featureData)
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            "Failed to find featureData for feature ID: ".concat(feature.id);

          // If feature data doesn't contain current discrete time - we can safely continue
          if (!(featureData !== null && featureData !== void 0 && featureData.intervals.contains(discreteTime.time))) return;
          const featureIdValue = featureData === null || featureData === void 0 || (_featureData$getValue2 = featureData.getValue(discreteTime.time)) === null || _featureData$getValue2 === void 0 ? void 0 : _featureData$getValue2.id;
          const style = enumStyles.find(s => s.value === featureIdValue);
          if (!style) throw "Failed to find style for feature ID: ".concat(feature.id, ", value: ").concat(featureIdValue);
          const failMessage = prop => "Failed to test feature ID: ".concat(feature.id, ", ID value: ").concat(style.value, ", time: ").concat(discreteTime.tag, ", property: ").concat(prop);
          expect((_feature$path = feature.path) === null || _feature$path === void 0 || (_feature$path = _feature$path.leadTime) === null || _feature$path === void 0 ? void 0 : _feature$path.getValue(discreteTime.time)).toBe(style.leadTime, failMessage("leadTime"));
          expect((_feature$path2 = feature.path) === null || _feature$path2 === void 0 || (_feature$path2 = _feature$path2.trailTime) === null || _feature$path2 === void 0 ? void 0 : _feature$path2.getValue(discreteTime.time)).toBe(style.trailTime, failMessage("trailTime"));
          expect((_feature$path3 = feature.path) === null || _feature$path3 === void 0 || (_feature$path3 = _feature$path3.width) === null || _feature$path3 === void 0 ? void 0 : _feature$path3.getValue(discreteTime.time)).toBe(style.width, failMessage("width"));
          expect((_feature$path4 = feature.path) === null || _feature$path4 === void 0 || (_feature$path4 = _feature$path4.resolution) === null || _feature$path4 === void 0 ? void 0 : _feature$path4.getValue(discreteTime.time)).toBe(style.resolution, failMessage("resolution"));
          const material = (_feature$path5 = feature.path) === null || _feature$path5 === void 0 || (_feature$path5 = _feature$path5.material) === null || _feature$path5 === void 0 ? void 0 : _feature$path5.getValue(discreteTime.time);
          if (material) {
            var _material$color;
            expect((_material$color = material.color) === null || _material$color === void 0 ? void 0 : _material$color.toCssColorString()).toBe(style.polylineGlow.color, failMessage("polylineGlow.color"));
            expect(material.glowPower).toBe(style.polylineGlow.glowPower, failMessage("polylineGlow.glowPower"));
            expect(material.taperPower).toBe(style.polylineGlow.taperPower, failMessage("polylineGlow.taperPower"));
          }
        });
      });
    });
    it("bin trail/path style with time-enabled points - dynamic styling", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonEnumDateIdCsv);
      const binStyles = [{
        maxValue: 50,
        leadTime: 0,
        trailTime: 10,
        width: 1,
        resolution: 40,
        polylineGlow: {
          color: "rgb(255,0,0)",
          glowPower: 0.1,
          taperPower: 0.4
        }
      }, {
        maxValue: 60,
        leadTime: 10,
        trailTime: 20,
        width: 2,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,255,0)",
          glowPower: 0.2,
          taperPower: 0.5
        }
      }, {
        maxValue: 70,
        leadTime: 20,
        trailTime: 60,
        width: 3,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,0,255)",
          glowPower: 0.3,
          taperPower: 0.6
        }
      }];
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        color: createStratumInstance(TableColorStyleTraits, {
          nullColor: "rgb(255,0,255)"
        }),
        trail: createStratumInstance(TableTrailStyleTraits, {
          column: "level",
          enabled: true,
          materialType: "polylineGlow",
          bin: [createStratumInstance(BinTrailSymbolTraits, binStyles[0]), createStratumInstance(BinTrailSymbolTraits, binStyles[1]), createStratumInstance(BinTrailSymbolTraits, binStyles[2])]
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      (await item.loadMapItems()).throwIfError();
      const mapItem = item.mapItems[0];

      // Time = level
      // 2015-08-01 = 50 (style 0)
      // 2015-08-02 = 60 (style 1)
      // 2015-08-04 = 70 (style 2)

      // We also check interpolated level at 2015-08-03
      // 2015-08-03 = 65 (style 1 into style 2)
      const interpolatedStyle = {
        maxValue: 65,
        leadTime: 15,
        trailTime: 40,
        width: 2.5,
        resolution: 30,
        polylineGlow: {
          color: "rgb(0,128,128)",
          glowPower: 0.25,
          taperPower: 0.55
        }
      };
      const times = [JulianDate.fromDate(new Date("2015-08-01")), JulianDate.fromDate(new Date("2015-08-02")), JulianDate.fromDate(new Date("2015-08-04")), JulianDate.fromDate(new Date("2015-08-03")) // Note interpolated date is last
      ];
      mapItem.entities.values.forEach(feature => {
        times.forEach((time, timeIndex) => {
          var _featureData$getValue3, _featureData$getValue4, _binStyles$timeIndex, _feature$path6, _feature$path7, _feature$path8, _feature$path9, _feature$path0;
          const featureData = feature.data.timeIntervalCollection;
          if (!featureData)
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            "Failed to find featureData for feature ID: ".concat(feature.id);
          const featureIdColumnValue = featureData === null || featureData === void 0 || (_featureData$getValue3 = featureData.getValue(time)) === null || _featureData$getValue3 === void 0 ? void 0 : _featureData$getValue3.id;

          // We only check features with id === "Feature A"
          // Otherwise the test would be too large
          if (featureIdColumnValue !== "feature A") return;
          const featureBinColumnValue = parseFloat(featureData === null || featureData === void 0 || (_featureData$getValue4 = featureData.getValue(time)) === null || _featureData$getValue4 === void 0 ? void 0 : _featureData$getValue4.level);

          // First three time indices map to binStyles - the last uses interpolatedStyle
          const style = (_binStyles$timeIndex = binStyles[timeIndex]) !== null && _binStyles$timeIndex !== void 0 ? _binStyles$timeIndex : interpolatedStyle;
          if (!style) throw "Failed to find style for feature ID: ".concat(feature.id, ", value: ").concat(featureBinColumnValue);
          const failMessage = prop => "Failed to test feature ID: ".concat(feature.id, ", ID value: ").concat(featureBinColumnValue, ", time: ").concat(time, ", property: ").concat(prop);
          expect((_feature$path6 = feature.path) === null || _feature$path6 === void 0 || (_feature$path6 = _feature$path6.leadTime) === null || _feature$path6 === void 0 ? void 0 : _feature$path6.getValue(time)).toBe(style.leadTime, failMessage("leadTime"));
          expect((_feature$path7 = feature.path) === null || _feature$path7 === void 0 || (_feature$path7 = _feature$path7.trailTime) === null || _feature$path7 === void 0 ? void 0 : _feature$path7.getValue(time)).toBe(style.trailTime, failMessage("trailTime"));
          expect((_feature$path8 = feature.path) === null || _feature$path8 === void 0 || (_feature$path8 = _feature$path8.width) === null || _feature$path8 === void 0 ? void 0 : _feature$path8.getValue(time)).toBe(style.width, failMessage("width"));
          expect((_feature$path9 = feature.path) === null || _feature$path9 === void 0 || (_feature$path9 = _feature$path9.resolution) === null || _feature$path9 === void 0 ? void 0 : _feature$path9.getValue(time)).toBe(style.resolution, failMessage("resolution"));
          const material = (_feature$path0 = feature.path) === null || _feature$path0 === void 0 || (_feature$path0 = _feature$path0.material) === null || _feature$path0 === void 0 ? void 0 : _feature$path0.getValue(time);
          if (material) {
            var _material$color2;
            expect((_material$color2 = material.color) === null || _material$color2 === void 0 ? void 0 : _material$color2.toCssColorString()).toBe(style.polylineGlow.color, failMessage("polylineGlow.color"));
            expect(material.glowPower).toBe(style.polylineGlow.glowPower, failMessage("polylineGlow.glowPower"));
            expect(material.taperPower).toBe(style.polylineGlow.taperPower, failMessage("polylineGlow.taperPower"));
          }
        });
      });
    });
    it("correctly applies disableDepthTestDistance trait", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        point: createStratumInstance(TablePointStyleTraits, {
          null: createStratumInstance(PointSymbolTraits, {
            disableDepthTestDistance: 42
          })
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      await item.loadMapItems();
      const mapItem = item.mapItems[0];
      mapItem.entities.values.forEach(entity => {
        var _entity$point;
        return expect((_entity$point = entity.point) === null || _entity$point === void 0 || (_entity$point = _entity$point.disableDepthTestDistance) === null || _entity$point === void 0 ? void 0 : _entity$point.getValue(JulianDate.now())).toBe(42);
      });
    });
    it("correctly applies scaleByDistance traits", async function () {
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "test-style",
        point: createStratumInstance(TablePointStyleTraits, {
          null: createStratumInstance(PointSymbolTraits, {
            scaleByDistance: createStratumInstance(ScaleByDistanceTraits, {
              near: 0,
              nearValue: 4,
              far: 50000,
              farValue: 10
            })
          })
        })
      })]);
      item.setTrait(CommonStrata.user, "activeStyle", "test-style");
      await item.loadMapItems();
      const mapItem = item.mapItems[0];
      mapItem.entities.values.forEach(entity => {
        var _entity$point2;
        return expect((_entity$point2 = entity.point) === null || _entity$point2 === void 0 || (_entity$point2 = _entity$point2.scaleByDistance) === null || _entity$point2 === void 0 ? void 0 : _entity$point2.getValue(JulianDate.now())).toEqual(jasmine.objectContaining({
          near: 0,
          nearValue: 4,
          far: 50000,
          farValue: 10
        }));
      });
    });
    it("doesn't pick hidden style as default activeStyle", async function () {
      item.setTrait(CommonStrata.user, "csvString", ParkingSensorDataCsv);
      (await item.loadMapItems()).throwIfError();
      expect(item.activeStyle).toBe("eventid");
      item.setTrait(CommonStrata.user, "styles", [createStratumInstance(TableStyleTraits, {
        id: "eventid",
        hidden: true
      })]);
      (await item.loadMapItems()).throwIfError();
      expect(item.activeStyle).toBe("parkflag");
    });
  });
  describe("applies default featureInfoTemplate", function () {
    it("removes _id_ from template", async function () {
      var _item$featureInfoTemp;
      item.setTrait(CommonStrata.user, "csvString", LatLonValCsv);
      (await item.loadMapItems()).throwIfError();
      expect((_item$featureInfoTemp = item.featureInfoTemplate.template) === null || _item$featureInfoTemp === void 0 ? void 0 : _item$featureInfoTemp.indexOf("_id_")).toBe(-1);
    });
  });
});